import { Button, DatePicker, Drawer, Form, Input, Select, Upload } from "antd";
import type { UploadProps, UploadFile } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import styles from "./styles.module.scss";
import {
  ICreationDetails,
  IImpotsForm,
  useAdministrationPlaces,
  useEditImpots
} from "../queries";
import { FieldData } from "rc-field-form/es/interface";
import moment from "moment";
import { useAuth } from "providers/auth";
import { useEffect, useState } from "react";

interface Props {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  data?: ICreationDetails["impot"];
}

const ImpotsDrawer: React.FC<Props> = ({ isOpen, onClose, id, data }) => {
  const [impotsForm] = Form.useForm();
  const { accessToken } = useAuth();
  const [impotFileList, setImpotFileList] = useState<
    UploadFile[] | undefined
  >();
  const [nifFileList, setNifFileList] = useState<UploadFile[] | undefined>();

  const { mutate, isLoading } = useEditImpots(id, {
    meta: { message: "Impots a bien etait modifié" },
    onSuccess() {
      closeDrawer();
    },
    onError(error: any) {
      let formErrors: FieldData[] = [];
      for (let key in error.response.data.errors) {
        formErrors.push({
          name: key,
          errors: [error.response.data.errors[key][0].message]
        });
      }
      impotsForm.setFields(formErrors);
    }
  });

  const { data: places } = useAdministrationPlaces({
    enabled: !!id
  });

  useEffect(() => {
    setImpotFileList(
      data
        ? [
            {
              uid: "2",
              name: data?.existence.split("/").slice(-1)[0],
              status: "success",
              url: `${data?.existence}?t=${btoa(accessToken!)}`
            }
          ]
        : undefined
    );
    setNifFileList(
      data
        ? [
            {
              uid: "2",
              status: "success",
              name: data?.nif.split("/").slice(-1)[0],
              url: `${data?.nif}?t=${btoa(accessToken!)}`
            }
          ]
        : undefined
    );
  }, [accessToken, data]);

  const onFinish = (values: IImpotsForm) => {
    const formData = new FormData();
    formData.append("start_date", values.start_date.format("YYYY-MM-DD"));
    formData.append("delivered_place_id", values.delivered_place_id);
    formData.append("article_number", values.article_number);
    formData.append("existence", values.existence?.file?.originFileObj || "");
    formData.append("nif_number", values.nif_number);
    formData.append("nif", values.nif?.file?.originFileObj || "");
    mutate(formData);
  };

  const closeDrawer = () => {
    onClose();
    impotsForm.resetFields();
  };

  const handleNifFileChange: UploadProps["onChange"] = (info) => {
    setNifFileList([...info.fileList]);
  };

  const handleImpotFileChange: UploadProps["onChange"] = (info) => {
    setImpotFileList([...info.fileList]);
  };
  return (
    <Drawer visible={isOpen} onClose={closeDrawer}>
      <Form
        className={styles.form}
        form={impotsForm}
        layout="vertical"
        onFinish={onFinish}
        initialValues={
          data
            ? {
                start_date: data?.start_date ? moment(data?.start_date) : null,
                delivered_place_id: data?.delivered_place.id,
                article_number: data?.article_number,
                nif_number: data?.nif_number
              }
            : undefined
        }
      >
        <Form.Item
          rules={[
            { required: true, message: "Veuillez selectionner une date !" }
          ]}
          label="Date de debut d'activité"
          name="start_date"
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="delivered_place_id"
          rules={[{ required: true, message: "Veuillez saisir un lieu !" }]}
          label="Ou délivré"
        >
          <Select placeholder="Veuillez sélectionner une adresse">
            {places?.map((place) => {
              if (place.type === "impôts") {
                return (
                  <Select.Option key={place.id} value={place.id}>
                    {place.address}
                  </Select.Option>
                );
              } else {
                return null;
              }
            })}
          </Select>
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: "Veuillez saisir le numéro de registre !"
            }
          ]}
          label="No d'article"
          name="article_number"
        >
          <Input />
        </Form.Item>
        <Form.Item
          rules={
            !data?.existence
              ? [
                  {
                    required: true,
                    message: "Veuillez saisir le fichier de registre !"
                  }
                ]
              : []
          }
          name="existence"
          label="Fichier d'existance"
        >
          <Upload.Dragger
            maxCount={1}
            customRequest={(options) =>
              setTimeout(() => options.onSuccess?.("ok"), 0)
            }
            onChange={handleImpotFileChange}
            fileList={impotFileList}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-hint">
              Cliquez ou faites glisser le fichier dans cette zone pour le
              télécharger
            </p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: "Veillez saisir le numero NIF"
            }
          ]}
          name="nif_number"
          label="No NIF"
        >
          <Input />
        </Form.Item>
        <Form.Item
          rules={
            !data?.nif
              ? [
                  {
                    required: true,
                    message: "Veillez saisir le fichier NIF"
                  }
                ]
              : []
          }
          name="nif"
          label="Fichier NIF"
        >
          <Upload.Dragger
            maxCount={1}
            customRequest={(options) =>
              setTimeout(() => options.onSuccess?.("ok"), 0)
            }
            onChange={handleNifFileChange}
            fileList={nifFileList}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-hint">
              Cliquez ou faites glisser le fichier dans cette zone pour le
              télécharger
            </p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item>
          <Button loading={isLoading} type="primary" htmlType="submit">
            Sauvgarder
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ImpotsDrawer;
